/**
 * A meeting bot the host never lets in (owner decision 2026-10-03).
 *
 * Recall gives up on a bot after 10 minutes in the meeting's waiting room
 * (salesguru-services `WAITING_ROOM_LIMIT_SECONDS`), or at once when the host
 * denies it. calendar_sync's `bot_terminated` then carries `notAdmitted: true`
 * and `notAdmittedReason` (services `utils/recall_not_admitted.py`). There was no
 * call, so the page shows "Bot not let in" and the reason instead of the
 * end-of-call review and summary. The desktop apps use the same reasons and copy
 * (macOS `BotLifecycle`, WPF `BotLifecycleLogic`).
 */

/** `bot_terminated.notAdmittedReason` values; anything else reads as `unknown`. */
export const BOT_NOT_ADMITTED_REASONS = ['host_removed', 'waiting_room_timeout', 'meeting_ended', 'unknown'] as const;

export type BotNotAdmittedReason = (typeof BOT_NOT_ADMITTED_REASONS)[number];

const NOT_ADMITTED_MESSAGES: Record<BotNotAdmittedReason, string> = {
  host_removed: "The host didn't let the bot in.",
  waiting_room_timeout: 'Nobody let the bot in within 10 minutes.',
  meeting_ended: 'The meeting ended before the bot was let in.',
  unknown: "The bot wasn't let into the meeting.",
};

export const BOT_NOT_ADMITTED_COPY = {
  title: 'Bot not let in',
  dismiss: 'Dismiss',
  messages: NOT_ADMITTED_MESSAGES,
} as const;

/**
 * Window event, dispatched for every bot that ended without being let in:
 * MeetBotModal stops waiting when it is the bot it created (the same pattern as
 * its `bot-active` / `bot-joined-meeting` events).
 */
export const BOT_NOT_ADMITTED_EVENT = 'bot-not-admitted';

export interface BotNotAdmittedEventDetail {
  botId: string;
  reason: BotNotAdmittedReason;
}

/** Recall's waiting-room limit (services `WAITING_ROOM_LIMIT_SECONDS`). */
export const RECALL_WAITING_ROOM_MS = 10 * 60 * 1000;

/**
 * How long MeetBotModal waits for the bot to be let in. Only a safety net for a
 * lost end signal: it runs past Recall's limit so Recall's end, with its reason,
 * arrives first. (It was 180 s, which gave up while the bot still waited.)
 */
export const BOT_ADMISSION_TIMEOUT_MS = RECALL_WAITING_ROOM_MS + 2 * 60 * 1000;
